import { StrictMode as _StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'

// CHUNK SFASATI DOPO UN DEPLOY (segnalazione 2026-07-14: "a volte non si scrive nei
// campi finche' non faccio Ctrl+F5"). Una scheda aperta durante un deploy tiene l'index
// vecchio ma lazy-carica i chunk NUOVI (o li manca del tutto, rsync --delete): il modulo
// non arriva, React rompe un sottoalbero in silenzio e i campi smettono di rispondere.
// Vite emette 'vite:preloadError' proprio per questo: ricarichiamo una volta sola
// (guard in sessionStorage per non entrare in loop se anche il reload fallisce).
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault(); // evita l'unhandled rejection
  const KEY = 'chunk_reload_at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last > 30_000) {
    sessionStorage.setItem(KEY, String(Date.now()));
    console.warn('[main] chunk non caricabile (deploy avvenuto?): ricarico la pagina');
    window.location.reload();
  }
});

// 🚨 PWA-CRITICAL — Subdomain detection + URL rewrite
// Se modifichi questo blocco, rifai i test PWA (iOS + Android) e lancia tests/smoke-pwa.sh.
// Documentazione: docs/PWA_PUSH_ARCHITECTURE.md
// ✅ SUBDOMAIN REDIRECT: su operator.*/officina.* riscriviamo l'URL al subpath corretto
// PRIMA che React Router monti. Così le route esistenti matchano e la PWA ha origin/scope "/" .
const _hostname = window.location.hostname;
const _isOperatorHost = _hostname.startsWith('operator.');
const _isOfficinaHost = _hostname.startsWith('officina.');
const _isPreventiviHost = _hostname.startsWith('preventivi.');

if (_isOperatorHost && !window.location.pathname.startsWith('/mobile-operator')) {
  const tail = window.location.pathname === '/' ? '/' : window.location.pathname;
  window.history.replaceState({}, '', '/mobile-operator' + tail + window.location.search + window.location.hash);
} else if (_isOfficinaHost && !window.location.pathname.startsWith('/mobile-officina')) {
  const tail = window.location.pathname === '/' ? '/' : window.location.pathname;
  window.history.replaceState({}, '', '/mobile-officina' + tail + window.location.search + window.location.hash);
} else if (_isPreventiviHost && !window.location.pathname.startsWith('/mobile-preventivi') && !window.location.pathname.startsWith('/login')) {
  // A differenza di operator/officina NON si conserva il tail: il login standard
  // fa replace('/dashboard') a pieno carico, che qui deve tornare alla webapp.
  window.history.replaceState({}, '', '/mobile-preventivi' + window.location.search + window.location.hash);
}

// ✅ MANIFEST DINAMICO: Cambia manifest basato su hostname o route.
// Sottodominio: manifest con scope "/" (manifest-*-sub.json).
// Dominio principale con subpath: manifest con scope "/mobile-*/".
const currentPath = window.location.pathname;
if (_isOfficinaHost) {
  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (manifestLink) manifestLink.setAttribute('href', '/manifest-officina-sub.json');
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute('content', '#ea580c');
} else if (_isOperatorHost) {
  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (manifestLink) manifestLink.setAttribute('href', '/manifest-operator-sub.json');
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute('content', '#1e40af');
} else if (currentPath.startsWith('/mobile-officina')) {
  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (manifestLink) manifestLink.setAttribute('href', '/manifest-officina.json');
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute('content', '#ea580c');
} else if (_isPreventiviHost) {
  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (manifestLink) manifestLink.setAttribute('href', '/manifest-preventivi-sub.json');
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute('content', '#2563eb');
} else if (currentPath.startsWith('/mobile-operator')) {
  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (manifestLink) manifestLink.setAttribute('href', '/manifest-mobile.json');
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute('content', '#1e40af');
} else if (currentPath.startsWith('/mobile-preventivi')) {
  const manifestLink = document.querySelector('link[rel="manifest"]');
  if (manifestLink) manifestLink.setAttribute('href', '/manifest-preventivi.json');
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute('content', '#2563eb');
}

// ✅ SERVICE WORKER ABILITATO PER NOTIFICHE PUSH
// Necessario per notifiche push su iOS e Android

// Registra Service Worker per PWA e notifiche push
// IMPORTANTE: Usa sw.js standard per installabilità PWA
// @ts-ignore - SW_VERSION per tracking versione service worker
const _SW_VERSION = '115'; // v116: Compact controls per Android
const SW_FILENAME = 'sw.js'; // ✅ Standard sw.js per PWA installabile
const SW_ENABLED = true; // ✅ ABILITATO PER PUSH NOTIFICATIONS

// PWA multi-install: ogni subdir registra il suo SW con scope dedicato.
// Questo permette a Chrome di trattare mobile-operator e mobile-officina
// come PWA DISTINTE installabili entrambe sullo stesso device.
const _currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
// Su sottodominio operator.*/officina.* lo scope è la root (ogni subdomain ha SW proprio).
// Sul dominio principale, invece, lo scope è il subdir (/mobile-*/).
const PWA_SCOPE =
  (_isOperatorHost || _isOfficinaHost) ? '/' :
  _currentPath.startsWith('/mobile-officina') ? '/mobile-officina/' :
  _currentPath.startsWith('/mobile-operator') ? '/mobile-operator/' :
  '/';
const SW_URL = `${PWA_SCOPE}${SW_FILENAME}`;

if ('serviceWorker' in navigator && SW_ENABLED) {
  window.addEventListener('load', async () => {
    try {
      // STEP 1: Unregister SW che conflittano con il nostro scope corrente.
      // - Se siamo in subdir PWA (/mobile-*/): rimuovi anche il SW root scope /
      //   (altrimenti Chrome vede 2 SW sullo stesso URL e non fa partire beforeinstallprompt)
      // - Negli altri casi rimuovi vecchi SW stesso scope con URL diverso.
      const registrations = await navigator.serviceWorker.getRegistrations();
      // const isSubdirPwa = PWA_SCOPE !== '/'; // rimosso: più no-unregister del SW root (fix push Android)
      for (const reg of registrations) {
        const scriptUrl = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || '';
        const regScopePath = new URL(reg.scope).pathname;

        // NOTA: NON unregistriamo più il SW root quando siamo in subdir PWA.
        // Android Chrome lega FCM al SW root originario; rimuoverlo spezza pushManager.subscribe
        // con "push service error". Il SW root resta attivo come fallback per push.
        // Vecchio SW dello stesso scope ma URL diverso dal nostro attuale → rimuovi
        if (scriptUrl && !scriptUrl.endsWith(SW_URL) && regScopePath === PWA_SCOPE) {
          console.log('[Main] 🗑️ Unregister vecchio SW stesso scope:', scriptUrl);
          await reg.unregister();
        }
      }

      // STEP 2: Registra SW scoped alla PWA corrente
      console.log(`[Main] 📝 Registro SW: ${SW_URL} (scope: ${PWA_SCOPE})`);
      const registration = await navigator.serviceWorker.register(SW_URL, { scope: PWA_SCOPE, updateViaCache: 'none' });

      // iOS PWA (fix 2026-07-23): i timer sono sospesi in background e il check
      // update non parte mai da congelata — forzalo quando l'app torna visibile
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') registration.update().catch(() => {});
      });

      // Forza update se disponibile (con error handling)
      try {
        await registration.update();
      } catch (updateError: any) {
        console.warn('[Main] ⚠️ Errore update iniziale SW (ignorato):', updateError.message);
      }

      // Se c'è un SW in waiting, attivalo immediatamente
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }

      // Controlla updates ogni 30 secondi (con error handling)
      setInterval(async () => {
        try {
          // Verifica che la registration sia ancora valida prima di fare update
          if (registration && registration.active) {
            await registration.update();
          }
        } catch (error: any) {
          // InvalidStateError: SW è stato unregistered o è in stato invalido
          // Questo è normale se l'utente ha navigato via o fatto refresh
          if (error.name === 'InvalidStateError') {
            console.warn('[Main] ⚠️ ServiceWorker in stato invalido, skip update');
          } else {
            console.error('[Main] Errore update ServiceWorker:', error);
          }
        }
      }, 30000);

      // LISTENER: Ricevi messaggi dal Service Worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('[Main] 📨 Messaggio ricevuto da SW:', event.data);

        // ✅ AGGRESSIVO: Auto-reload quando SW si aggiorna
        if (event.data && event.data.type === 'SW_UPDATED') {
          console.log(`[Main] 🔄 Service Worker aggiornato a ${event.data.version || 'nuova versione'}!`);
          console.log('[Main] 🔄 Ricarico pagina automaticamente...');

          // Ricarica immediatamente (SW già attivato e claiming clients)
          window.location.reload();
        }
      });

    } catch (error) {
      console.error('Errore registrazione Service Worker:', error);
    }
  });
}

// Unregister tutti i Service Worker esistenti al caricamento (sviluppo)
if ('serviceWorker' in navigator && !SW_ENABLED) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    for (const reg of registrations) {
      console.log('[Main] 🧹 Unregistering SW per sviluppo:', reg.scope);
      reg.unregister();
    }
  });
}

createRoot(document.getElementById('root')!).render(
  <App />
)
